import { Injectable } from '@angular/core';
import { Observable, of, switchMap, map, catchError, throwError, tap, take } from 'rxjs';
import * as lodash from 'lodash';
import * as moment from 'moment';

import { LoopbackCrudApiService } from './loopback-crud-api.service';
import { AiFunctionCallControllerService, WorkflowManagementControllerService } from '@rappider/rappider-sdk';
import { th } from 'date-fns/locale';

@Injectable({
  providedIn: 'root'
})
export class UIComponentDataProviderService {

  private aiFunctionIdCache = new Map<string, string>();

  /**
   * The constructor is updated to inject the new WorkflowManagementControllerService.
   */
  constructor(
    private loopbackCrudApiService: LoopbackCrudApiService,
    private aiFunctionCallControllerService: AiFunctionCallControllerService,
    private workflowManagementControllerService: WorkflowManagementControllerService
  ) { }

  /**
   * Gets and transforms data for a specific entity.
   * This version safely handles cases where no transformation function is provided.
   *
   * @param entityName The name of the entity to fetch data from.
   * @param apiGetFilter An object used to filter the API GET request.
   * @param postDataTransformationFunctionAsString An optional string representation of a function to transform the data.
   * @returns An Observable that emits the (potentially transformed) data array.
   */
  public getEntityData(
    entityName: string,
    apiGetFilter: object,
    postDataTransformationFunctionAsString?: string // Parameter is now optional
  ): Observable<any[]> {
    // If no transformation string is provided, call the API without a transformer function.
    // This is the main fix to prevent creating a broken function from an undefined string.
    if (!postDataTransformationFunctionAsString) {
      return this.loopbackCrudApiService.fetchDataFromEntityApiAndTransform(
        entityName,
        apiGetFilter,
        undefined // Explicitly pass undefined for the function argument
      );
    } else {
      // If a transformation string IS provided, create the dynamic function.
      const postDataTransformationFunction = (data: any[]): any[] => {
        if (postDataTransformationFunctionAsString.includes('lodash')) {
          return new Function('data', 'lodash', `return ${postDataTransformationFunctionAsString};`)(data, lodash);
        } else {
          return new Function('data', `return ${postDataTransformationFunctionAsString};`)(data);
        }
      };

      // Call the API service with the newly created function.
      return this.loopbackCrudApiService.fetchDataFromEntityApiAndTransform(
        entityName,
        apiGetFilter,
        postDataTransformationFunction
      );
    }
  }

  /**
   * Gets a response from a named AI function, using a cache for function IDs.
   */
  public getAiResponse(aiFunctionName: string, prompt: string): Observable<any> {
    return this.getAiFunctionIdWithName(aiFunctionName).pipe(
      switchMap(aiFunctionId => {
        if (!aiFunctionId) {
          return throwError(() => new Error(`AI function with name '${aiFunctionName}' not found.`));
        }
        const aiFunctionCallParams = {
          id: aiFunctionId,
          body: { functionPrompt: prompt }
        };
        return this.aiFunctionCallControllerService.executeFunctionToolsCall$Json(aiFunctionCallParams);
      }),
      catchError(error => {
        console.error(`Error in getAiResponse for function '${aiFunctionName}':`, error);
        return throwError(() => error);
      })
    );
  }

  public getAiResponseForEntityFilter(prompt: string): Observable<any> {
    const aiFunctionNameForEntityFilter = 'uiComponentDataProvider_entityFilterGenerator';
    return this.getAiResponse(aiFunctionNameForEntityFilter, prompt).pipe(
      map(response => response),
      catchError(error => {
        console.error('Error in getAiResponseForEntityFilter:', error);
        return throwError(() => error);
      })
    );
  }

  // #region NEW FUNCTION

  /**
   * Executes a custom-defined lambda function via the workflow management service.
   *
   * @param {string} serviceName The name of the service containing the custom function (e.g., 'UiDataProvider').
   * @param {string} functionName The name of the custom function to execute (e.g., 'test1').
   * @param {object} [payload] An optional payload object to send to the custom function. Defaults to an empty object if not provided.
   * @returns {Observable<any>} An Observable that emits the response from the custom function.
   */
  public getCustomFunctionResponse(serviceName: string, functionName: string, payload?: object): Observable<any> {
    const customFunctionExecuteParams = {
      serviceName,
      functionName,
      // If the payload is null or undefined, default it to an empty object.
      payload: payload ?? {}
    };

    return this.workflowManagementControllerService.runCustomFunctionDefinitionWithJWT({ body: customFunctionExecuteParams })
      .pipe(
        catchError(error => {
          console.error(`Error executing custom function '${functionName}' in service '${serviceName}':`, error);
          // Forward the error to the component subscriber for handling.
          return throwError(() => error);
        })
      );
  }

  // #endregion

  /**
   * (Private) Fetches an AI function ID by its name, with memoization.
   */
  private getAiFunctionIdWithName(aiFunctionName: string): Observable<string | null> {
    if (this.aiFunctionIdCache.has(aiFunctionName)) {
      const cachedId = this.aiFunctionIdCache.get(aiFunctionName);
      console.log(`CACHE HIT: Found ID for '${aiFunctionName}' in cache.`);
      return of(cachedId ?? null);
    }

    console.log(`CACHE MISS: Fetching ID for '${aiFunctionName}' from API.`);
    const filter = {
      where: { name: aiFunctionName },
      fields: ['id']
    };

    return this.aiFunctionCallControllerService.find({ filter }).pipe(
      map(response => response?.[0]?.id ?? null),
      tap(id => {
        if (id) {
          this.aiFunctionIdCache.set(aiFunctionName, id);
        }
      }),
      catchError(error => {
        console.error(`Error fetching AI function ID for '${aiFunctionName}':`, error);
        return of(null);
      })
    );
  }

  // #region NEW CHAINING FUNCTIONS

  /**
   * Chains an AI call with an entity data call. It uses the AI's response as the filter for fetching entity data.
   *
   * @param {string} aiFunctionName The name of the AI function to call for getting the filter.
   * @param {string} prompt The prompt to send to the AI function (e.g., "Get all sensors with critical status").
   * @param {string} entityName The name of the entity to fetch data from.
   * @param {string} postDataTransformationFunctionAsStringForEntityData A string representation of the function to transform the final entity data.
   * @param {string} postDataTransformationFunctionAsStringForResponse A string representation of the function to transform the final response data.
   * @returns {Observable<any[]>} An Observable that emits the final, transformed entity data.
   */
  public getEntityDataFromAiFilterResponse(
    prompt: string,
    entityName: string,
    postDataTransformationFunctionAsStringForEntityData?: string,
    postDataTransformationFunctionAsStringForResponse?: string,
  ): Observable<{ filter: any, data: any[] }> {
    console.log('🚀 Chained call: AI Response -> Entity Data');
    // 1. Get the dynamic filter from the AI function.
    return this.getAiResponseForEntityFilter(prompt).pipe(
      switchMap(aiResponseAsFilter => {
        console.log('AI provided filter:', aiResponseAsFilter);
        // Validate that the AI returned a usable object for the filter.
        if (typeof aiResponseAsFilter !== 'object' || aiResponseAsFilter === null || !aiResponseAsFilter.filter) {
          return throwError(() => new Error('AI response was not a valid filter object.'));
        }
        // 2. Use the AI response as the filter to get the entity data.
        return this.getEntityData(entityName, aiResponseAsFilter.filter, postDataTransformationFunctionAsStringForEntityData).pipe(
          map(entityData => {
            console.log('Entity data retrieved:', entityData);
            return {
              ...aiResponseAsFilter,
              data: entityData
            };
          }
          )
        );
      }),
      catchError(error => {
        console.error('Error in chained call \'getEntityDataFromAiResponse\':', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Chains an AI call with a custom function call. It uses the AI's response as the payload for the custom function.
   *
   * @param {string} aiFunctionName The name of the AI function to call for getting the payload.
   * @param {string} prompt The prompt to send to the AI function (e.g., "Prepare a summary for user John Doe").
   * @param {string} serviceName The name of the service containing the custom function.
   * @param {string} functionName The name of the custom function to execute.
   * @returns {Observable<any>} An Observable that emits the final response from the custom function.
   */
  public getCustomFunctionResponseFromAiPayloadResponse(
    aiFunctionName: string,
    prompt: string,
    serviceName: string,
    functionName: string
  ): Observable<any> {
    console.log('🚀 Chained call: AI Response -> Custom Function');
    // 1. Get the dynamic payload from the AI function.
    return this.getAiResponse(aiFunctionName, prompt).pipe(
      switchMap(aiResponseAsPayload => {
        console.log('AI provided payload:', aiResponseAsPayload);
        // Validate that the AI returned a usable object for the payload.
        if (typeof aiResponseAsPayload !== 'object' || aiResponseAsPayload === null) {
          return throwError(() => new Error('AI response was not a valid payload object.'));
        }
        // 2. Use the AI response as the payload to call the custom function.
        return this.getCustomFunctionResponse(serviceName, functionName, aiResponseAsPayload);
      }),
      catchError(error => {
        console.error('Error in chained call \'getCustomFunctionResponseFromAi\':', error);
        return throwError(() => error);
      })
    );
  }

  // #endregion


  /**
   * Dynamically executes a function provided as a string to transform an array of data.
   * This function can intelligently inject 'lodash' and 'moment' dependencies into the
   * scope of the executed function if they are detected in the string.
   *
   * @warning This function uses the `new Function()` constructor, which is a form of eval()
   * and can execute arbitrary code. It should **NEVER** be used with strings from untrusted
   * sources (e.g., user input) as it can pose a serious security risk (XSS).
   * The `transformFnAsString` should only come from a trusted, sanitized source.
   *
   * @param data The input data array to be transformed.
   * @param transformFnAsString A string that evaluates to a function.
   * E.g., `(data, lodash) => lodash.map(data, 'name')`
   * @returns The transformed data as an array. Returns an empty array if the
   * transformation fails or the result is null.
   */
  public runStringDataTransformationFunctionOnProvidedData(
    data: any[] | any,
    transformFnAsString: string
  ): any {
    // 1. Detect if the function string references lodash or moment to inject them as dependencies.
    // The regex checks for the word 'lodash' or the common alias '_' not part of another word.
    const usesLodash =
      /\blodash\b/.test(transformFnAsString) || /(^|[^\w.])_([^\w]|$)/.test(transformFnAsString);
    const usesMoment = /\bmoment\b/.test(transformFnAsString);

    try {
      // 2. Prepare the arguments and their values for the dynamic function constructor.
      // This creates a scope containing the necessary dependencies.
      const argNames: string[] = ['data'];
      const argValues: any[] = [data];

      if (usesLodash) {
        argNames.push('lodash');
        argValues.push(lodash);
      }
      if (usesMoment) {
        argNames.push('moment');
        argValues.push(moment);
      }


      // Find the index of the first occurrence of '=>', if so remove the arrow and fn definition
      const arrowIndex = transformFnAsString.indexOf('=>');

      // Get the part of the string *after* the arrow, and trim any leading whitespace
      const functionBody = transformFnAsString.substring(arrowIndex + 2).trim();

      console.log(functionBody);

      // 3. Construct a secure wrapper to execute the user's function string.
      // This wrapper ensures the string evaluates to a function before calling it
      // and dynamically builds the argument list for the call.
      const functionCallArguments = ['data', usesLodash ? 'lodash' : null, usesMoment ? 'moment' : null]
        .filter(Boolean)
        .join(', ');

      const wrapperSource = `
      const userFunction = (${functionCallArguments}) => (${transformFnAsString});
      if (typeof userFunction !== 'function') {
        throw new Error('The provided transformation string did not evaluate to a function.');
      }
      return userFunction(${functionCallArguments});
    `;

      // 4. Execute the wrapper with the prepared arguments and scope.
      const resultFn = new Function(...argNames, wrapperSource)(...argValues);

      const result = resultFn(data);

      return result || {};
    } catch (err) {
      // 6. If any part of the execution fails, log the error and return a safe, empty array.
      console.error('Data transformation failed:', err);
      return {};
    }
  }

  /**
     * Performs a full, AI-driven data fetching and transformation pipeline.
     *
     * This is a multi-step process:
     * 1. An initial AI call generates a database filter from a natural language prompt.
     * 2. The filter is used to fetch data from a specified entity.
     * 3. The retrieved data is then sent to a second AI to generate a transformation function string.
     * 4. This function string is executed to format the entity data into the final desired component data shape.
     *
     * @param {string} aiPrompt A natural language prompt describing the data needed (e.g., "Show me the top 5 most recent errors").
     * @param {string} entityName The name of the entity to fetch the initial data from (e.g., 'SensorData').
     * @returns {Observable<any[]>} An observable that emits the final, transformed data ready for a component.
     */
  // public getComponentConfigUsingAi2(
  //   aiPrompt: string,
  //   entityName: string
  // ): Observable<any> {
  //   console.log('🚀 Starting full AI-driven data pipeline...');

  //   // The entire multi-step, asynchronous process is flattened into a single, readable RxJS pipe.
  //   return this.getEntityDataFromAiFilterResponse(aiPrompt, entityName).pipe(
  //     // Step 1: The above function completes, emitting an object like { filter: {...}, data: [...] }.
  //     // We now use switchMap to pass this result to the next asynchronous step.
  //     switchMap(entityResponse => {
  //       // Validate the response from the first step before proceeding.
  //       if (!entityResponse?.data) {
  //         return throwError(() => new Error('Step 1 failed: AI-filtered entity data could not be retrieved.'));
  //       }

  //       const newComponentDataConfig = entityResponse;
  //       console.log('[SUCCESS] Step 1 completed. Component config and filter:', newComponentDataConfig);

  //       // Prepare the data from step 1 to be used as a prompt for step 2.
  //       const stringifiedComponentDataConfig = JSON.stringify(newComponentDataConfig, null, 2);
  //       const aiComponentDTFunctionName = 'uiComponentDataProvider_cardListDataTransformationFnBuilder';

  //       // Step 2: Call the second AI to get the data transformation function string.
  //       // We return this new observable to the chain.
  //       return this.getAiResponse(aiComponentDTFunctionName, stringifiedComponentDataConfig).pipe(
  //         // We need both the original data and the new transformation function for the next step,
  //         // so we use 'map' to combine them into a single object.
  //         map(dtResponse => ({
  //           entityData: entityResponse.data, // The data from Step 1.
  //           transformationFunction: dtResponse?.inputDataTransformationFunction // The function string from Step 2.
  //         }))
  //       );
  //     }),

  //     // The value emitted here is now the object { entityData, transformationFunction }.
  //     // Step 3: Execute the final data transformation. This step is synchronous, so we use 'map'.
  //     map(({ entityData, transformationFunction }) => {
  //       if (!transformationFunction) {
  //         // If the AI fails to return a function string, we can handle it gracefully.
  //         console.warn('AI did not provide a transformation function. Returning raw entity data.');
  //         return entityData;
  //       }

  //       // Combine newComponentDataConfig and transformationFunction
  //       const combinedComponentConfig = {
  //         ...newComponentDataConfig,
  //         transformationFunction: transformationFunction
  //       };


  //       console.log('AI provided transformation function:', transformationFunction);

  //       console.log('Applying final AI-generated data transformation...');
  //       // BUG FIX: We now correctly pass 'entityData' (the array) instead of the whole response.
  //       const finalComponentData = this.runStringDataTransformationFunctionOnProvidedData(
  //         entityData,
  //         transformationFunction
  //       );

  //       console.log('✅ [SUCCESS] AI pipeline complete. Final component data:', finalComponentData);
  //       return {
  //         componentInputDataConfig: {},
  //         componentInputData: finalComponentData
  //       };
  //     }),

  //     // A single, top-level error handler for the entire chain.
  //     // If any step fails, this block will catch it.
  //     catchError(error => {
  //       console.error('❌ [ERROR] The AI-driven data pipeline failed:', error);
  //       return throwError(() => error);
  //     })
  //   );
  // }



  /**
   * Performs a full, AI-driven pipeline to generate a component's configuration and its transformed data.
   *
   * This is a multi-step process:
   * 1. An initial AI call generates a component configuration (including a database filter) from a natural language prompt.
   * 2. The filter is used to fetch raw data from a specified entity.
   * 3. The retrieved config and data are then sent to a second AI to generate a transformation function string.
   * 4. This function string is executed to format the raw data into the final component data shape.
   *
   * @param {string} aiPrompt A natural language prompt describing the data needed (e.g., "Show me the top 5 most recent errors").
   * @param {string} entityName The name of the entity to fetch the initial data from (e.g., 'SensorData').
   * @returns {Observable<{componentInputDataConfig: object, componentInputData: any[]}>} An observable that emits a single object
   * containing both the final component configuration and the transformed data.
   */
  public getComponentConfigUsingAi(
    aiPrompt: string,
    entityName: string
  ): Observable<{ componentInputDataConfig: object; componentInputData: object }> {
    console.log('🚀 Starting full AI-driven data pipeline...');

    return this.getEntityDataFromAiFilterResponse(aiPrompt, entityName).pipe(
      // Step 1: This function gets the initial component config (with a filter) and uses it to fetch raw entity data.
      // It emits an object like { filter: {...}, data: [...] }.
      switchMap(initialResponse => {
        if (!initialResponse?.data) {
          return throwError(() => new Error('Step 1 failed: AI-filtered entity data could not be retrieved.'));
        }

        console.log('[SUCCESS] Step 1 completed. Initial config and data:', initialResponse);
        const stringifiedResponse = JSON.stringify(initialResponse, null, 2);
        const aiComponentDTFunctionName = 'uiComponentDataProvider_cardListDataTransformationFnBuilder';

        // Step 2: Call the second AI to get the data transformation function string.
        return this.getAiResponse(aiComponentDTFunctionName, stringifiedResponse).pipe(
          // We now pass the *full* initial response forward along with the new transformation function.
          // This solves the scope bug.
          map(dtResponse => ({
            initialConfigAndData: initialResponse, // The full object from Step 1.
            transformationFunction: dtResponse?.inputDataTransformationFunction // The function string from Step 2.
          }))
        );
      }),

      // The value emitted here is { initialConfigAndData, transformationFunction }.
      // Step 3: Combine the config, execute the transformation, and return the final object.
      map(({ initialConfigAndData, transformationFunction }) => {
        // Create the combined config object you wanted. It contains the initial AI response
        // plus the new transformation function string from the second AI call.
        const combinedComponentConfig = {
          ...initialConfigAndData,
          transformationFunction: transformationFunction
        };

        // Check if the transformation function is available
        if (!transformationFunction) {
          // If not, log a warning and return the untransformed data
          console.warn('AI did not provide a transformation function. Returning raw data.');
          return {
            componentInputDataConfig: combinedComponentConfig,
            componentInputData: initialConfigAndData.data // Return the untransformed data
          };
        } else {
          // There is a transformation function
          console.log('Applying final AI-generated data transformation...');
          const finalComponentData = this.runStringDataTransformationFunctionOnProvidedData(
            initialConfigAndData.data, // Use the raw data for the transformation
            transformationFunction
          );

          console.log('✅ [SUCCESS] AI pipeline complete.');
          // Return the final, structured object.
          return {
            componentInputDataConfig: combinedComponentConfig,
            componentInputData: finalComponentData || undefined
          };
        }


      }),

      // A single, top-level error handler for the entire chain.
      catchError(error => {
        console.error('❌ [ERROR] The AI-driven data pipeline failed:', error);
        return throwError(() => error);
      })
    );
  }

}
